// Live chat streams, held outside the React tree (ported from FRaiME).
//
// A streamed answer must survive ordinary navigation: switching tab or
// session, or leaving the project page, unmounts the chat view. So the stream
// lives in this module singleton rather than in component state or a context:
//   - nothing can unmount it, including a route change,
//   - only components that subscribe re-render, not the whole app per token.
//
// The remaining loss path is a refresh or tab close, which really does sever
// the connection. The beforeunload warning below covers it, and the backend
// persists a partial turn as status="interrupted" when the client disconnects.

import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";

import { messagesKey, sessionsKey } from "./chatKeys";
import type { AppliedFilters, ChatMessage, ChatSession } from "./chatApi";
import { chatApi } from "./chatApi";

export interface StreamChatState {
  pending: boolean;
  streamingText: string;
  streamingThinking: string;
  error: string | null;
  /** The stream ended without its assistant_message (Stop, dropped connection).
   *  The partial text stays on screen until the persisted interrupted row arrives. */
  stopped: boolean;
  /** Retrieval route reported by content-intelligence ("graph" | "grep" | "rag" | "full"). */
  route: string | null;
  /** Latest progress message (e.g. "Checking project references"). */
  statusMessage: string | null;
}

// Shared frozen snapshot for "no stream for this session". A fresh object here
// would make useSyncExternalStore re-render forever.
const IDLE: StreamChatState = Object.freeze({
  pending: false,
  streamingText: "",
  streamingThinking: "",
  error: null,
  stopped: false,
  route: null,
  statusMessage: null,
});

// Coalesce delta notifications so a long answer doesn't re-render (and
// re-parse markdown) per token. A timer, not rAF: rAF crawls in hidden tabs.
const NOTIFY_THROTTLE_MS = 50;

// How long to wait for the backend's interrupted row, which is written from the
// server's disconnect teardown and can land slightly after our abort.
const RECONCILE_DELAYS_MS = [500, 1500];

interface StreamRun {
  controller: AbortController;
  queryClient: QueryClient;
  projectId: string;
  token: number;
}

const entries = new Map<string, StreamChatState>();
const runs = new Map<string, StreamRun>();
const listeners = new Set<() => void>();
let nextToken = 1;
let notifyHandle: ReturnType<typeof setTimeout> | null = null;

// ── Subscription plumbing ────────────────────────────────────────────────────

function flush() {
  if (notifyHandle !== null) {
    clearTimeout(notifyHandle);
    notifyHandle = null;
  }
  for (const listener of listeners) listener();
}

function notify(immediate: boolean) {
  if (immediate) {
    flush();
    return;
  }
  if (notifyHandle !== null) return;
  notifyHandle = setTimeout(flush, NOTIFY_THROTTLE_MS);
}

/** Stable identity — useSyncExternalStore re-subscribes if this changes. */
export function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getStreamState(sessionId: string): StreamChatState {
  return entries.get(sessionId) ?? IDLE;
}

export function isStreaming(sessionId: string): boolean {
  return entries.get(sessionId)?.pending ?? false;
}

export function isAnyStreaming(): boolean {
  for (const entry of entries.values()) if (entry.pending) return true;
  return false;
}

/** True while any session belonging to this project is streaming. */
export function isProjectStreaming(projectId: string): boolean {
  for (const [sessionId, run] of runs) {
    if (run.projectId === projectId && isStreaming(sessionId)) return true;
  }
  return false;
}

function patchEntry(
  sessionId: string,
  patch: Partial<StreamChatState>,
  { immediate = false }: { immediate?: boolean } = {},
) {
  const prev = entries.get(sessionId) ?? IDLE;
  // Always a new object: the snapshot must be immutable for React to see it.
  entries.set(sessionId, { ...prev, ...patch });
  notify(immediate);
}

function clearEntry(sessionId: string) {
  if (!entries.delete(sessionId)) return;
  notify(true);
}

// ── beforeunload: a refresh or tab close does kill the stream ────────────────

function warnBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
  e.returnValue = "";
}

function syncBeforeUnloadListener() {
  if (typeof window === "undefined") return;
  window.removeEventListener("beforeunload", warnBeforeUnload);
  if (isAnyStreaming()) window.addEventListener("beforeunload", warnBeforeUnload);
}

// ── Starting, stopping, reconciling ──────────────────────────────────────────

export interface StartStreamParams {
  sessionId: string;
  projectId: string;
  /** Held across unmounts. Safe: the QueryClient is an app-level singleton. */
  queryClient: QueryClient;
  content: string;
  documentIds?: string[];
  appliedFilters?: AppliedFilters | null;
}

/**
 * Start a streamed turn. Resolves when the stream ends; rejects if it failed,
 * so the caller can restore the user's draft. Aborting via stopStream is not a
 * failure. A second call for a session that is already streaming is ignored.
 */
export async function startStream(params: StartStreamParams): Promise<void> {
  const { sessionId, projectId, queryClient: qc, content, documentIds, appliedFilters } = params;
  if (isStreaming(sessionId)) return;

  const controller = new AbortController();
  const token = nextToken++;
  runs.set(sessionId, { controller, queryClient: qc, projectId, token });
  patchEntry(
    sessionId,
    { pending: true, streamingText: "", streamingThinking: "", error: null, stopped: false, route: null, statusMessage: null },
    { immediate: true },
  );
  syncBeforeUnloadListener();

  try {
    await chatApi.streamMessage(
      sessionId,
      content,
      {
        onUserMessage: (message) => {
          qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => [...(prev ?? []), message]);
        },
        onSessionUpdate: ({ id, title }) => {
          qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
            (prev ?? []).map((s) => (s.id === id ? { ...s, title } : s)),
          );
        },
        onThinking: (text) => {
          const prev = getStreamState(sessionId);
          patchEntry(sessionId, { streamingThinking: prev.streamingThinking + text });
        },
        onDelta: (text) => {
          const prev = getStreamState(sessionId);
          patchEntry(sessionId, { streamingText: prev.streamingText + text, statusMessage: null });
        },
        onAssistantMessage: (message) => {
          const { route } = getStreamState(sessionId);
          qc.setQueryData<ChatMessage[]>(messagesKey(sessionId), (prev) => [...(prev ?? []), { ...message, route }]);
          // The persisted row carries the text now; drop the live copy.
          clearEntry(sessionId);
          qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
        },
        onError: (detail) => {
          patchEntry(
            sessionId,
            { pending: false, streamingText: "", streamingThinking: "", statusMessage: null, error: detail },
            { immediate: true },
          );
        },
        onDone: () => {
          // Still pending only when the stream ended without a terminal event —
          // a dropped connection. Treat it like a stop.
          if (!isStreaming(sessionId)) return;
          patchEntry(sessionId, { pending: false, stopped: true }, { immediate: true });
          void reconcileInterrupted(sessionId, token);
        },
        onStatus: ({ phase, route, message }) => {
          if (phase === "routing" && route) patchEntry(sessionId, { route }, { immediate: true });
          if (message) patchEntry(sessionId, { statusMessage: message }, { immediate: true });
        },
      },
      { documentIds, appliedFilters, signal: controller.signal },
    );
  } catch (err) {
    if (controller.signal.aborted) return; // stopStream already set the state
    const message = err instanceof Error ? err.message : "Failed to send message";
    patchEntry(
      sessionId,
      { pending: false, streamingText: "", streamingThinking: "", statusMessage: null, error: message },
      { immediate: true },
    );
    throw err;
  } finally {
    if (runs.get(sessionId)?.token === token) runs.delete(sessionId);
    syncBeforeUnloadListener();
  }
}

/**
 * Abort a stream at the user's request. Keeps the partial text on screen and
 * refetches: the backend persists what it generated as an interrupted turn,
 * and that row is what should end up in the thread.
 */
export function stopStream(sessionId: string): void {
  const run = runs.get(sessionId);
  if (!run) {
    clearEntry(sessionId);
    return;
  }
  run.controller.abort();
  patchEntry(sessionId, { pending: false, stopped: true }, { immediate: true });
  syncBeforeUnloadListener();
  void reconcileInterrupted(sessionId, run.token);
}

async function reconcileInterrupted(sessionId: string, token: number) {
  const run = runs.get(sessionId);
  const qc = run?.queryClient;
  const projectId = run?.projectId;
  if (!qc) {
    clearEntry(sessionId);
    return;
  }
  try {
    for (const delay of RECONCILE_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      // A new turn started in the meantime — its state owns this session now.
      if (runs.get(sessionId)?.token !== token && isStreaming(sessionId)) return;
      await qc.refetchQueries({ queryKey: messagesKey(sessionId) });
      const messages = qc.getQueryData<ChatMessage[]>(messagesKey(sessionId));
      if (messages?.[messages.length - 1]?.role === "assistant") break;
    }
    if (projectId) qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
  } catch {
    // Offline, or the session is gone; fall through and clear the local copy.
  } finally {
    if (!isStreaming(sessionId)) clearEntry(sessionId);
  }
}

/** Abort everything — on sign-out, when the token is being torn down. */
export function abortAllStreams(): void {
  for (const run of runs.values()) run.controller.abort();
  runs.clear();
  entries.clear();
  syncBeforeUnloadListener();
  notify(true);
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Full live state for a session's stream. Re-renders as text arrives. */
export function useChatStream(sessionId: string): StreamChatState {
  return useSyncExternalStore(subscribe, () => getStreamState(sessionId), () => IDLE);
}

/** Whether this session is streaming (a boolean, so indicators don't re-render per delta). */
export function useIsStreaming(sessionId: string): boolean {
  return useSyncExternalStore(subscribe, () => isStreaming(sessionId), () => false);
}

/** Whether this project has a stream running — for hints outside the chat view. */
export function useIsProjectStreaming(projectId: string): boolean {
  return useSyncExternalStore(subscribe, () => isProjectStreaming(projectId), () => false);
}
